/**
 * HTML sanitisation for the reader and the compose editor (spec §10.1).
 *
 * Two separate DOMPurify instances: the reader one carries the `afterSanitizeAttributes` hook
 * (link hardening, `cid:` rewrite, remote image/style blocking); the compose one is a plain
 * allow-list so signatures and pasted content keep basic formatting only.
 */
import createDOMPurify from 'dompurify';

type Purifier = ReturnType<typeof createDOMPurify>;

let readerPurifier: Purifier | null = null;
let composePurifier: Purifier | null = null;
/** cid → attachment URL for the message being rendered (set per `sanitizeReader` call). */
let cidMap = new Map<string, string>();

const REMOTE_URL_RE = /url\(\s*['"]?https?:/i;
const REMOTE_URL_ALL_RE = /url\(\s*['"]?https?:[^)]*\)/gi;

function getReader(): Purifier {
  if (readerPurifier) return readerPurifier;
  const p = createDOMPurify(window);
  p.addHook('afterSanitizeAttributes', (node) => {
    if (node.tagName === 'A') {
      node.setAttribute('target', '_blank');
      node.setAttribute('rel', 'noopener noreferrer');
    }
    if (node.tagName === 'IMG') {
      const src = node.getAttribute('src') || '';
      if (/^cid:/i.test(src)) {
        const url = cidMap.get(src.slice(4).replace(/[<>]/g, ''));
        if (url) node.setAttribute('src', url);
        else node.remove();
      } else if (/^https?:\/\//i.test(src)) {
        // Remote images are privacy beacons: park the URL so "show images" can restore it.
        node.setAttribute('data-blocked-src', src);
        node.removeAttribute('src');
      } else if (!src.startsWith('data:image/')) {
        node.remove();
      }
    }
    const style = node.getAttribute?.('style');
    if (style && REMOTE_URL_RE.test(style)) {
      node.setAttribute('data-blocked-style', style);
      node.setAttribute('style', style.replace(REMOTE_URL_ALL_RE, 'none'));
    }
  });
  readerPurifier = p;
  return p;
}

function getCompose(): Purifier {
  if (!composePurifier) composePurifier = createDOMPurify(window);
  return composePurifier;
}

export interface InlineAttachment {
  cid: string;
  url: string;
}

export interface SanitizedBody {
  html: string;
  blockedImages: number;
}

/** Reader sanitiser: returns the clean html and the count of blocked remote images/styles. */
export function sanitizeReader(html: string, inline: readonly InlineAttachment[] = []): SanitizedBody {
  cidMap = new Map(inline.map((a) => [a.cid.replace(/[<>]/g, ''), a.url]));
  const clean = String(
    getReader().sanitize(html, {
      FORBID_TAGS: ['form', 'input', 'button', 'iframe', 'object', 'embed', 'script', 'link', 'meta', 'base'],
      FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover'],
      ALLOW_DATA_ATTR: false,
      ADD_ATTR: ['data-blocked-src', 'data-blocked-style'],
      WHOLE_DOCUMENT: false,
    }),
  ).replace(/(<style[^>]*>)([\s\S]*?)(<\/style>)/gi, (_m, open: string, css: string, close: string) => open + css.replace(REMOTE_URL_ALL_RE, 'none') + close);
  const blockedImages = (clean.match(/data-blocked-src=/g) || []).length + (clean.match(/data-blocked-style=/g) || []).length;
  return { html: clean, blockedImages };
}

const escapeHtml = (s: string) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
const linkify = (s: string) =>
  s.replace(/(https?:\/\/[^\s<]+)/g, '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>');

/**
 * Plain text → html: escaped, URLs auto-linked, consecutive `>` lines grouped into one
 * `<blockquote>`, wrapped in `<div dir="auto" style="white-space:pre-wrap">`.
 */
export function textToHtml(text: string): string {
  const parts: string[] = [];
  let quote: string[] = [];
  const flush = () => {
    if (quote.length) {
      parts.push('<blockquote>' + quote.join('\n') + '</blockquote>');
      quote = [];
    }
  };
  for (const line of (text || '').split('\n')) {
    const m = /^\s*>\s?(.*)$/.exec(line);
    if (m) quote.push(linkify(escapeHtml(m[1]!)));
    else {
      flush();
      parts.push(linkify(escapeHtml(line)));
    }
  }
  flush();
  let out = '';
  for (const p of parts) {
    if (p.startsWith('<blockquote>')) out += p;
    else out += (out && !out.endsWith('</blockquote>') ? '\n' : '') + p;
  }
  return `<div dir="auto" style="white-space:pre-wrap">${out}</div>`;
}

/** Compose-side allow-list sanitiser for draft/reply html and the outgoing body. */
export function sanitizeCompose(html: string): string {
  return String(
    getCompose().sanitize(html, {
      ALLOWED_TAGS: ['p', 'div', 'br', 'b', 'strong', 'i', 'em', 'u', 's', 'ul', 'ol', 'li', 'a', 'img', 'blockquote', 'span', 'h1', 'h2', 'h3', 'pre', 'code'],
      ALLOWED_ATTR: ['href', 'src', 'alt', 'dir', 'style'],
      ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel|cid):|[^a-z]|[a-z+.-]+(?:[^a-z+.:-]|$))/i,
    }),
  );
}

/** Styles injected into the reader's shadow root. HTML mail always sits on a white "paper". */
export const READER_SHADOW_CSS = `
    :host { display: block; }
    * { max-width: 100%; }
    body, div { font-family: inherit; }
    a { color: #0b5ed7; }
    /* HTML mail is authored for a white background: most bodies declare a text colour
       (color:#000 in Outlook signatures, tables, <font>) and no background. On the dark app
       surface that becomes black-on-dark (measured 1.26:1 and 1.52:1), i.e. unreadable. So the
       body is shown on a fixed light "paper" in both themes: it preserves the sender's intent
       and keeps the message legible. */
    .tik-paper { background: #fff; color: #111827; padding: 14px; border-radius: 10px;
      box-shadow: 0 0 0 1px rgba(17,24,39,.10); }
    pre { overflow-x: auto; }
    blockquote { border-inline-start: 3px solid #a8b2c1; margin-inline: 0; padding-inline-start: 12px; opacity: .82; }
    .tik-qtoggle { display: inline-flex; margin: 10px 0; padding: 4px 14px;
      border: 1px solid rgba(128,134,148,.45); border-radius: 999px; background: transparent;
      color: inherit; opacity: .75; font: inherit; font-size: 12px; cursor: pointer; }
    .tik-qtoggle:hover { opacity: 1; }
`;

/** Text length above which an outermost quote is collapsed. */
export const QUOTE_COLLAPSE_MIN_CHARS = 120;

/**
 * Hide every outermost `blockquote` / gmail quote with ≥ 120 chars of text behind a
 * `button.tik-qtoggle` (`"••• " + showQuoted/hideQuoted`). Returns the number collapsed.
 */
export function collapseQuotes(root: ParentNode, labels: { show: string; hide: string }): number {
  const container = (root as Element).lastElementChild ?? (root as Element);
  if (!container) return 0;
  const targets: Element[] = [];
  for (const q of container.querySelectorAll('blockquote, [class*="gmail_quote"]')) {
    if (targets.some((t) => t.contains(q))) continue;
    if ((q.textContent ?? '').trim().length < QUOTE_COLLAPSE_MIN_CHARS) continue;
    targets.push(q);
  }
  for (const q of targets) {
    const el = q as HTMLElement;
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'tik-qtoggle';
    btn.textContent = '••• ' + labels.show;
    el.style.display = 'none';
    btn.onclick = () => {
      const hidden = el.style.display === 'none';
      el.style.display = hidden ? '' : 'none';
      btn.textContent = '••• ' + (hidden ? labels.hide : labels.show);
    };
    el.parentNode?.insertBefore(btn, el);
  }
  return targets.length;
}

/** Restore blocked remote images/styles ("show images"). Returns how many images were unblocked. */
export function unblockImages(root: ParentNode): number {
  let n = 0;
  root.querySelectorAll('img[data-blocked-src]').forEach((img) => {
    img.setAttribute('src', img.getAttribute('data-blocked-src') || '');
    img.removeAttribute('data-blocked-src');
    n++;
  });
  root.querySelectorAll('[data-blocked-style]').forEach((el) => {
    el.setAttribute('style', el.getAttribute('data-blocked-style') || '');
    el.removeAttribute('data-blocked-style');
  });
  return n;
}

/**
 * Render sanitised html into an open shadow root on `host` (reusing `existing` when it belongs
 * to the same host) and collapse long quotes. Returns the shadow root.
 */
export function renderIntoShadow(
  host: HTMLElement,
  html: string,
  isHtml: boolean,
  labels: { show: string; hide: string },
  existing?: ShadowRoot | null,
): ShadowRoot {
  const root = existing && existing.host === host ? existing : host.attachShadow({ mode: 'open' });
  root.innerHTML = `<style>${READER_SHADOW_CSS}</style><div class="${isHtml ? 'tik-paper' : ''}" dir="auto" style="line-height:1.75; word-break:break-word">${html}</div>`;
  collapseQuotes(root, labels);
  return root;
}

/** Text of the rendered body (for quoting in replies): `innerText` of the paper div. */
export function renderedText(root: ShadowRoot | null | undefined): string {
  const el = root?.lastElementChild as HTMLElement | null | undefined;
  return (el?.innerText ?? el?.textContent ?? '').trim();
}

/** First 140 chars of a body as a list snippet (html tags stripped, whitespace collapsed). */
export function snippetOf(body: { type: string; content: string } | null | undefined, max = 140): string {
  if (!body) return '';
  const text = body.type === 'text/plain' ? body.content : (body.content || '').replace(/<[^>]+>/g, ' ');
  return text.replace(/\s+/g, ' ').trim().slice(0, max);
}
